import { isChangingPage } from '#app/components/utils';
import { isTruthy } from '@/utils';
import type { RouteLocationNormalizedGeneric } from 'vue-router';

function shouldScrollToTop(to: RouteLocationNormalizedGeneric) {
  return unref(to)
    .matched.map((page: any) => Object.values(page?.components))
    .flat()
    .some((component: any) => component?.scrollToTop);
}

/**
 * Checks whether current route needs a custom nuxt hook handling.
 *
 * Used for this pages paths so far:
 *  - url.account.orders
 *  - url.search
 *  - all categories pages - /c/
 */
const needsCustomHookHandle = (to: RouteLocationNormalizedGeneric) => {
  const { $i18n } = useNuxtApp();
  const isCategoryPage = '/c/';
  const isOrdersOverviewPage = to.path === $i18n.t('url.account.orders');
  const isPlpOrSearchPage = to.path.startsWith(isCategoryPage) || to.path === $i18n.t('url.search');

  return isOrdersOverviewPage || isPlpOrSearchPage;
};

/**
 * Gets proper OrderCard component hash while navigating back from /my-account/orders/detail/
 * to /my-account/orders/ since the browser's back navigation may result in a missing or outdated hash
 * which cause a wrong scroll position at the end.
 */
const getCorrectOrderCardElHash = (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric) => {
  const { $i18n } = useNuxtApp();
  const needsToUpdateElHash =
    to.path !== $i18n.t('url.account.orders') && !from.path.includes($i18n.t('url.account.orderDetails'));

  if (needsToUpdateElHash || !from.params?.order) {
    return undefined;
  }

  return `#order-${from.params.order}`;
};

const getFinalHash = (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric) => {
  return getCorrectOrderCardElHash(to, from) || to;
};

const getPositionFromHash = (to: RouteLocationNormalizedGeneric | string) => {
  const hash = typeof to === 'string' ? to : `#${window.CSS.escape(to.hash.substring(1))}`;
  const el = document.querySelector(hash) as HTMLElement;

  if (!el) {
    return {};
  }

  const position = { el: hash };

  // Add offset based on scroll-margin-top if present
  const scrollMarginTop = parseFloat(getComputedStyle(el).scrollMarginTop || '0');
  if (scrollMarginTop) {
    position.offset = { y: scrollMarginTop };
  }

  return position;
};

/* @see https://router.vuejs.org/api/interfaces/RouterOptions.html */
export default <any>{
  scrollBehavior(to: any, _from: any, savedPosition: any) {
    // Bail early
    if (!isChangingPage(to, _from)) {
      return Promise.resolve();
    }

    // Scroll bypass via `preventScrollToTop` query param
    if (isTruthy(to.query.preventScrollToTop?.toString()?.toLowerCase())) {
      return Promise.resolve();
    }

    let position: any = {};

    // Use browser navigation/history savedPosition,
    // or check if we should scroll to top
    if (savedPosition) {
      position = savedPosition;
    } else if (!to.hash || shouldScrollToTop(to)) {
      position = { left: 0, top: 0, behavior: 'instant' };
    }

    const nuxtApp = useNuxtApp();
    const hasTransition = (route: RouteLocationNormalizedGeneric) => !!route.meta.pageTransition;

    // Get the hook we need to wait for
    const pageHook = hasTransition(_from) && hasTransition(to) ? 'page:transition:finish' : 'page:finish';
    /**
     * In some cases (eg: a lot of elements to display) for:
     *  - Order History overview,
     *  - PLP pages
     * using 'page:transition' or 'page:finish' isn't sufficient as scrolling happens too early,
     * before all elements are fully rendered, which leads to incorrect scroll positioning at the end.
     *
     * Emitting a custom nuxt hook 'scroll:content:loaded' within the watchPostEffect vue watcher
     * seems to fix scroll issues as callback is called after vue updated DOM
     */
    const scrollContentLoadedHook = 'scroll:content:loaded';

    return new Promise((resolve) => {
      const hookHandle = async () => {
        // Ensure Vue rendering and JS event loop has finished before processing the scroll logic
        await nextTick();
        await timeout(0);

        // Check if we have a hash and override scroll position if the element for it exists
        if (to.hash) {
          position = getPositionFromHash(getFinalHash(to, _from));
        }

        // Resolve with scroll position
        resolve(position);
      };

      const hookToHandle = needsCustomHookHandle(to) ? scrollContentLoadedHook : pageHook;
      nuxtApp.hooks.hookOnce(hookToHandle, hookHandle);
    });
  },
};
